import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlanGate } from "@/components/PlanGate";
import { NewQuoteDialog } from "@/modules/quotes/NewQuoteDialog";
import { QuickChips } from "@/components/layout/Record";
import { useGarage } from "@/context/GarageContext";
import { FileText } from "lucide-react";
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { fmtCents, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

const FILTERS = ["", "draft", "sent", "accepted", "declined", "expired"];

export default function Quotes() {
  return <PlanGate feature="quotes_and_invoices"><QuotesList /></PlanGate>;
}

function QuotesList() {
  const { isManager, readOnly } = useGarage();
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const navigate = useNavigate();
  const { data, error, loading, reload } = useFetch("/quotes", { status });
  return (
    <div>
      <PageHeader title={t("quotes.title")} subtitle={t("quotes.subtitle")} actions={isManager && !readOnly && <Button onClick={() => setOpen(true)} data-testid="new-quote-button"><Plus className="mr-2 h-4 w-4" /> {t("quotes.new")}</Button>} />
      {open && <NewQuoteDialog onClose={() => setOpen(false)} onDone={(q) => navigate(`/pro/quotes/${q.id}`)} />}
      <div className="mb-5"><QuickChips testPrefix="quotes-filter" value={status} onChange={(s) => setParams(s ? { status: s } : {})} options={FILTERS.map((s) => [s, s ? t(`status.${s}`) : t("quotes.all")])} /></div>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState icon={FileText} title={t("quotes.empty")} text={t("quotes.emptyText")} testId="quotes-empty" />}
      {data?.length > 0 && (
        <div className="gf-card divide-y divide-slate-100" data-testid="quotes-list">
          {data.map((q) => (
            <button key={q.id} type="button" onClick={() => navigate(`/pro/quotes/${q.id}`)} data-testid={`quote-row-${q.number}`}
              className="grid w-full grid-cols-2 items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 md:grid-cols-[130px_1fr_1fr_120px_120px]">
              <span className="font-mono text-sm font-semibold">{q.number} <span className="text-slate-400">v{q.version}</span></span>
              <span className="text-sm">{q.customer.name}</span>
              <span className="text-sm text-slate-600">{q.vehicle ? `${q.vehicle.plate} · ${q.vehicle.make} ${q.vehicle.model}` : "—"}</span>
              <span className="text-sm font-semibold md:text-right">{fmtCents(q.total_cents)}<span className="block text-[11px] font-normal text-slate-400">{q.valid_until && fmtYmd(q.valid_until)}</span></span>
              <span className="md:text-right"><StatusBadge status={q.status} /></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
